import { useEffect, useMemo, useRef, useState } from 'react'

import { api } from '../api/client'
import type { OggettoTaggabile, TagCatalogo, TagPiattaforma } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { ErrorBanner } from './common'

/**
 * I tag nella console: il distintivo, l'editor di un oggetto, la cella «Tag»
 * degli elenchi e il catalogo in memoria per suggerire i tag già usati.
 *
 * Un tag è «chiave:valore» (o solo «chiave»), con il colore della chiave.
 * Quelli messi da una regola hanno un segno (⚙) e si possono togliere, ma la
 * regola li rimetterà al giro dopo se l'oggetto la soddisfa ancora: lo dice il
 * titolo. Vedi control-plane/app/services/tag_service.py.
 */

/** Testo scuro o chiaro sul colore del tag, per il contrasto. */
function inchiostro(colore: string): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(colore)
  if (!m) return '#fff'
  const [r, g, b] = [m[1], m[2], m[3]].map((x) => parseInt(x, 16))
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? '#111' : '#fff'
}

export function TagChip({
  tag,
  onRemove,
  piccolo = false,
}: {
  tag: Pick<TagPiattaforma, 'key' | 'value' | 'color' | 'source'>
  onRemove?: () => void
  piccolo?: boolean
}) {
  const regola = tag.source === 'rule'
  return (
    <span
      className={`tag-chip${piccolo ? ' tag-chip--piccolo' : ''}`}
      style={{ background: tag.color, color: inchiostro(tag.color) }}
      title={regola ? t('Messo da una regola: se lo togli, la regola lo rimette finché l’oggetto la soddisfa.') : undefined}
    >
      {regola && <span aria-hidden="true">⚙ </span>}
      <span className="tag-chip__chiave">{tag.key}</span>
      {tag.value && <span className="tag-chip__valore">{tag.value}</span>}
      {onRemove && (
        <button type="button" className="tag-chip__via" onClick={onRemove} aria-label={t('Togli il tag')}>
          ×
        </button>
      )}
    </span>
  )
}

/** Il catalogo dei tag, per i suggerimenti. */
/**
 * The tag filter of a list (the CI list), in two pieces: the menu that adds a
 * tag (in the filter bar, as tall as the other fields) and the chosen tags with
 * «tutti / almeno uno» (in the list header, next to the count). The value is the
 * comma-separated labels the API reads (`tag=a,b` with `tag_mode=all|any`).
 */
function scelti(valore: string): string[] {
  return valore.split(',').map((x) => x.trim()).filter(Boolean)
}

export function MenuTagFiltro({ valore, catalogo, tipo, onCambia }: {
  valore: string
  catalogo: TagCatalogo[]
  tipo: OggettoTaggabile
  onCambia: (valore: string) => void
}) {
  const presi = scelti(valore)
  const giaPresi = new Set(presi.map((x) => x.toLowerCase()))
  const liberi = catalogo.filter((c) => (c.counts[tipo] ?? 0) > 0 && !giaPresi.has(c.label.toLowerCase()))
  return (
    <select value="" aria-label={t('Aggiungi un tag al filtro')}
            onChange={(e) => { if (e.target.value) onCambia([...presi, e.target.value].join(',')) }}>
      <option value="">{presi.length ? t('+ un altro tag…') : t('Tutti')}</option>
      {liberi.map((c) => <option key={c.id} value={c.label}>{c.label} ({c.counts[tipo]})</option>)}
    </select>
  )
}

export function TagFiltrati({ valore, modo, catalogo, onCambia }: {
  valore: string
  modo: 'all' | 'any'
  catalogo: TagCatalogo[]
  onCambia: (valore: string, modo: 'all' | 'any') => void
}) {
  const presi = scelti(valore)
  if (!presi.length) return null
  const perEtichetta = new Map(catalogo.map((c) => [c.label.toLowerCase(), c]))
  const scrivi = (lista: string[], m: 'all' | 'any' = modo) => onCambia(lista.join(','), m)
  return (
    <div className="filtro-tag">
      {presi.map((s) => {
        const c = perEtichetta.get(s.toLowerCase())
        const [chiave, ...resto] = s.split(':')
        return (
          <TagChip key={s} piccolo
                   tag={{ key: c?.key ?? chiave, value: c?.value ?? resto.join(':'), color: c?.color ?? '#d0d7de', source: 'manual' }}
                   onRemove={() => scrivi(presi.filter((x) => x !== s))} />
        )
      })}
      {presi.length >= 2 && (
        <div className="segmented segmented--piccolo" role="group" aria-label={t('Come combinare i tag')}>
          <button type="button" className={modo === 'all' ? 'segmented__on' : ''} onClick={() => scrivi(presi, 'all')}
                  title={t('Solo i CI che hanno tutti questi tag')}>{t('tutti')}</button>
          <button type="button" className={modo === 'any' ? 'segmented__on' : ''} onClick={() => scrivi(presi, 'any')}
                  title={t('I CI che hanno almeno uno di questi tag')}>{t('almeno uno')}</button>
        </div>
      )}
      <button type="button" className="linklike" style={{ fontSize: 12 }} onClick={() => scrivi([], 'all')}>
        {t('togli i tag')}
      </button>
    </div>
  )
}

export function useCatalogoTag() {
  return useAsync(() => api.tags(), [])
}

/**
 * Il campo per aggiungere un tag: scrivi «chiave:valore» (o scegli un tag già
 * usato dai suggerimenti) e Invio.
 */
function AggiungiTag({
  catalogo,
  esclusi,
  onAggiungi,
}: {
  catalogo: TagCatalogo[]
  esclusi: string[]
  onAggiungi: (etichetta: string) => Promise<void>
}) {
  const [testo, setTesto] = useState('')
  const [lavoro, setLavoro] = useState(false)
  const campo = useRef<HTMLInputElement>(null)
  const suggeriti = useMemo(() => {
    const q = testo.trim().toLowerCase()
    return catalogo
      .filter((c) => !esclusi.includes(c.label) && (!q || c.label.toLowerCase().includes(q)))
      .slice(0, 8)
  }, [catalogo, esclusi, testo])

  async function aggiungi(etichetta: string) {
    if (!etichetta.trim()) return
    setLavoro(true)
    try {
      await onAggiungi(etichetta.trim())
      setTesto('')
      campo.current?.focus()
    } finally {
      setLavoro(false)
    }
  }

  return (
    <div className="tag-aggiungi">
      <input
        ref={campo}
        value={testo}
        disabled={lavoro}
        onChange={(e) => setTesto(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void aggiungi(testo)
          }
        }}
        placeholder={t('chiave:valore, poi Invio')}
        aria-label={t('Aggiungi un tag')}
        list={undefined}
      />
      {suggeriti.length > 0 && (
        <div className="tag-aggiungi__suggeriti">
          {suggeriti.map((c) => (
            <button type="button" key={c.id} className="tag-aggiungi__voce" onClick={() => void aggiungi(c.label)}>
              <TagChip tag={c} piccolo />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * I tag di un oggetto, con aggiungi e togli (per chi può scrivere sul modulo
 * Tag). Per le schede di dettaglio: CI, asset, business service.
 */
export function EditorTag({ tipo, id, onChanged }: { tipo: OggettoTaggabile; id: string; onChanged?: () => void }) {
  const miei = useAsync(() => api.tagsOf(tipo, [id]), [tipo, id])
  const catalogo = useCatalogoTag()
  const permessi = usePermessiModuli()
  const [errore, setErrore] = useState<unknown>(null)
  const tags = miei.data?.[id] ?? []
  const scrive = permessi.scrive('tags')
  if (!permessi.vede('tags')) return null

  async function cambia(fare: () => Promise<unknown>) {
    setErrore(null)
    try {
      await fare()
      await Promise.all([miei.reload(), catalogo.reload()])
      onChanged?.()
    } catch (err) {
      setErrore(err)
    }
  }

  return (
    <div className="tag-editor">
      <ErrorBanner error={errore} />
      <div className="tag-editor__elenco">
        {tags.length === 0 && !scrive && <span className="muted">{t('Nessun tag')}</span>}
        {tags.map((tg) => (
          <TagChip
            key={tg.id}
            tag={tg}
            onRemove={scrive ? () => void cambia(() => api.unassignTags(tipo, [id], [tg.id])) : undefined}
          />
        ))}
      </div>
      {scrive && (
        <AggiungiTag
          catalogo={catalogo.data ?? []}
          esclusi={tags.map((x) => x.label)}
          onAggiungi={(etichetta) => cambia(() => api.assignTags(tipo, [id], { tags: [etichetta] }))}
        />
      )}
    </div>
  )
}

/**
 * I tag di tutti gli oggetti di un tipo, per la colonna «Tag» degli elenchi:
 * una richiesta sola, e il testo su cui la colonna si filtra («ambiente:prod»).
 */
export function useTagDi(tipo: OggettoTaggabile) {
  const permessi = usePermessiModuli()
  const vede = permessi.vede('tags')
  const mappa = useAsync(
    () => (vede ? api.tagsOf(tipo) : Promise.resolve({} as Record<string, TagPiattaforma[]>)),
    [tipo, vede],
  )
  return {
    vede,
    scrive: permessi.scrive('tags'),
    di: (id: string): TagPiattaforma[] => mappa.data?.[id] ?? [],
    testo: (id: string) => (mappa.data?.[id] ?? []).map((x) => x.label).join(' '),
    reload: mappa.reload,
  }
}

/** La cella «Tag» di un elenco: i distintivi e, per chi scrive, un ✎ che apre l'editor sul posto. */
export function CellaTag({
  tipo,
  id,
  tags,
  scrive,
  onChanged,
}: {
  tipo: OggettoTaggabile
  id: string
  tags: TagPiattaforma[]
  scrive: boolean
  onChanged: () => void
}) {
  const [aperto, setAperto] = useState(false)
  const box = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!aperto) return
    const fuori = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setAperto(false)
    }
    document.addEventListener('mousedown', fuori)
    return () => document.removeEventListener('mousedown', fuori)
  }, [aperto])
  return (
    <span className="tag-cella" ref={box}>
      {tags.map((tg) => (
        <TagChip key={tg.id} tag={tg} piccolo />
      ))}
      {scrive && (
        <button type="button" className="link tag-cella__modifica" onClick={() => setAperto((x) => !x)}
                aria-label={t('Modifica i tag')} title={t('Modifica i tag')}>
          ✎
        </button>
      )}
      {aperto && (
        <span className="tag-cella__popover">
          <EditorTag tipo={tipo} id={id} onChanged={onChanged} />
        </span>
      )}
    </span>
  )
}

/**
 * La colonna «Tag» pronta per una DataTable: filtrabile come le altre (sul
 * testo «chiave:valore»), non ordinabile. Assente se il ruolo nasconde i tag.
 */
export function colonnaTag<T>(
  tipo: OggettoTaggabile,
  tag: ReturnType<typeof useTagDi>,
  idDi: (riga: T) => string,
) {
  if (!tag.vede) return []
  return [
    {
      key: '__tag',
      header: t('Tag'),
      sort: false,
      value: (riga: T) => tag.testo(idDi(riga)),
      render: (riga: T) => (
        <CellaTag tipo={tipo} id={idDi(riga)} tags={tag.di(idDi(riga))} scrive={tag.scrive}
                  onChanged={() => void tag.reload()} />
      ),
    },
  ]
}
